import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the context
const LogoContext = createContext();

// Custom hook to use the logo context
export const useLogo = () => {
  const context = useContext(LogoContext);
  if (!context) {
    throw new Error('useLogo must be used within a LogoProvider');
  }
  return context;
};

// Provider component
export const LogoProvider = ({ children }) => {
  const [logo, setLogo] = useState(null);
  const [logoName, setLogoName] = useState('');
  const [logoSize, setLogoSize] = useState(0);
  const [saveStatus, setSaveStatus] = useState('');

  // Load saved logo on provider mount
  useEffect(() => {
    const savedLogo = sessionStorage.getItem('schoolLogo');
    const savedLogoName = sessionStorage.getItem('schoolLogoName');
    const savedLogoSize = sessionStorage.getItem('schoolLogoSize');
    
    if (savedLogo) {
      setLogo(savedLogo);
      setLogoName(savedLogoName || 'Unknown');
      const parsedSize = parseInt(savedLogoSize, 10);
      setLogoSize(Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : 120);
    }
  }, []);

  // Save logo to session storage
  const saveLogo = (logoData, name, size) => {
    try {
      const displaySize = Number.isFinite(size) && size > 0 ? size : logoSize || 120;
      sessionStorage.setItem('schoolLogo', logoData);
      sessionStorage.setItem('schoolLogoName', name);
      sessionStorage.setItem('schoolLogoSize', displaySize.toString());

      setLogo(logoData);
      setLogoName(name);
      setLogoSize(displaySize);
      setSaveStatus('saved');
      
      setTimeout(() => setSaveStatus(''), 2000);
      return true;
    } catch (error) {
      console.error('Error saving logo:', error);
      return false;
    }
  };

  // Remove logo from session storage
  const removeLogo = () => {
    sessionStorage.removeItem('schoolLogo');
    sessionStorage.removeItem('schoolLogoName');
    sessionStorage.removeItem('schoolLogoSize');
    
    setLogo(null);
    setLogoName('');
    setLogoSize(0);
    setSaveStatus('removed');
    
    setTimeout(() => setSaveStatus(''), 2000);
  };

  // Get logo HTML for exports
  const getLogoHtml = (className = 'logo') => {
    if (!logo) return '';
    const maxHeight = logoSize || 80;
    return `<img src="${logo}" alt="School Logo" class="${className}" style="max-height: ${maxHeight}px; margin-bottom: 10px;" />`;
  };

  // Check if logo exists
  const hasLogo = Boolean(logo);

  const value = {
    logo,
    logoName,
    logoSize,
    setLogoSize,
    saveStatus,
    setSaveStatus,
    saveLogo,
    removeLogo,
    getLogoHtml,
    hasLogo
  };

  return (
    <LogoContext.Provider value={value}>
      {children}
    </LogoContext.Provider>
  );
};